import { useRef, useEffect } from "react";
import {
  ArrowLeft, Save, Check, Mail, Phone, MapPin,
  Users, Building2, ArrowRightLeft, UserRound,
} from "lucide-react";
import './customer-workspace.css';

export default function CustomerEditor({ form, setForm, editMode, saving, onSave, onClose }) {
  const initial = useRef(JSON.stringify(form));
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const contact = (key, value) =>
    setForm((prev) => ({ ...prev, contact_person: { ...prev.contact_person, [key]: value } }));

  const close = () => {
    if (saving) return;
    if (initial.current === JSON.stringify(form) || window.confirm("Kaydedilmemiş değişiklikler var. Çıkmak istediğinize emin misiniz?"))
      onClose();
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const warn = (e) => {
      if (initial.current !== JSON.stringify(form)) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [form]);

  const isCompany = form.customer_type === "company";
  const balance = Number(form.opening_balance) || 0;
  const RoleIcon = form.contact_role === 'supplier' ? Building2 : form.contact_role === 'both' ? ArrowRightLeft : Users;
  const iconColor = form.contact_role === 'supplier' ? '#d97706' : form.contact_role === 'both' ? '#7c3aed' : '#1487a3';
  const iconBg = form.contact_role === 'supplier' ? '#fef3c7' : form.contact_role === 'both' ? '#ede9fe' : '#e2f4f8';
  const roleLabel = form.contact_role === "supplier" ? "Tedarikçi" : form.contact_role === "both" ? "Müşteri & Tedarikçi" : "Müşteri";

  const Field = ({ label, value, onChange, wide, hint, multiline, ...rest }) => (
    <label className={`cw-field${wide ? ' wide' : ''}`}>
      <span>{label}</span>
      {multiline
        ? <textarea value={value || ""} onChange={e => onChange(e.target.value)} rows={3} {...rest} />
        : <input value={value ?? ""} onChange={e => onChange(e.target.value)} {...rest} />
      }
      {hint && <small>{hint}</small>}
    </label>
  );

  return (
    <div className="cw">
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'16px',marginBottom:'24px',paddingBottom:'20px',borderBottom:'1px solid var(--cw-line)',flexWrap:'wrap'}}>
        <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
          <button type="button" onClick={close} disabled={saving} className="cw-nav-back">
            <ArrowLeft size={14} />
          </button>
          <div>
            <h1 style={{fontFamily:'Manrope,Inter,system-ui,sans-serif',fontSize:'20px',fontWeight:750,letterSpacing:'-0.018em',lineHeight:1.2,color:'var(--cw-ink)',margin:0}}>{editMode ? "Cari Hesabı Düzenle" : "Yeni Cari Hesap"}</h1>
            <p style={{fontSize:'12px',color:'var(--cw-muted)',marginTop:'3px',marginBottom:0}}>{editMode ? "Düzenleme modu" : "Yeni cari hesap"}</p>
          </div>
        </div>
        <div className="cw-actions">
          <button type="button" onClick={close} disabled={saving}>Vazgeç</button>
          <button type="button" onClick={onSave} disabled={saving} className="cw-primary">
            <Save size={14} />
            {saving ? "Kaydediliyor…" : editMode ? "Değişiklikleri Kaydet" : "Cari Hesabı Oluştur"}
          </button>
        </div>
      </div>

      <div className="cw-editor-layout">
        <form onSubmit={onSave} className="cw-editor-main">

          <div id="s01" className="cw-form-section">
            <div className="cw-section-title">
              <span>01</span>
              <div><h2>Hesabın Kimliği</h2><p>Bu hesapla nasıl çalışıyorsunuz?</p></div>
            </div>
            <p className="cw-choice-label">Cari Rolü</p>
            <div className="cw-choices">
              {[
                ["customer", "Müşteri", "Satış yaptığınız kişi", Users],
                ["supplier", "Tedarikçi", "Alım yaptığınız firma", Building2],
                ["both", "Her ikisi", "İki yönlü ticaret", ArrowRightLeft],
              ].map(([v, l, hint, Icon]) => (
                <button key={v} type="button" aria-pressed={form.contact_role === v}
                  onClick={() => update("contact_role", v)} className={form.contact_role === v ? "active" : ""}>
                  <Icon size={16} />
                  <span><strong>{l}</strong><small>{hint}</small></span>
                </button>
              ))}
            </div>
            <div className="cw-segment">
              {[["individual", "Gerçek kişi", UserRound], ["company", "Tüzel kişi", Building2]].map(([v, l, Icon]) => (
                <button key={v} type="button" aria-pressed={form.customer_type === v}
                  onClick={() => update("customer_type", v)} className={form.customer_type === v ? "active" : ""}>
                  <Icon size={13} /> {l}
                </button>
              ))}
            </div>
            <div className="cw-field-grid">
              <Field label={isCompany ? "Firma Ünvanı *" : "Ad Soyad *"} value={form.name} onChange={v => update("name", v)} placeholder={isCompany ? "Firma veya kurumun tam ünvanı" : "Ad ve soyad"} required autoFocus wide />
              <Field label="Kısa İsim" value={form.short_name} onChange={v => update("short_name", v)} placeholder="Listede kolayca tanıyın" />
              <Field label={isCompany ? "Vergi Kimlik No" : "TC Kimlik No"} value={form.tax_no} onChange={v => update("tax_no", v)} maxLength={16} />
              <Field label="Vergi Dairesi" value={form.tax_office} onChange={v => update("tax_office", v)} />
              <Field label="Kategori" value={form.category} onChange={v => update("category", v)} placeholder="Örn. Bayi, kurumsal, perakende" />
            </div>
          </div>

          <div id="s02" className="cw-form-section">
            <div className="cw-section-title">
              <span>02</span>
              <div><h2>İletişim ve Adres</h2><p>Fatura ve yazışmalar için kullanılacak bilgiler.</p></div>
            </div>
            <div className="cw-field-grid">
              <Field label="E-posta" type="email" autoComplete="email" value={form.email} onChange={v => update("email", v)} placeholder="ornek@firma.com" />
              <Field label="Telefon" type="tel" autoComplete="tel" value={form.phone} onChange={v => update("phone", v)} placeholder="0 (5xx) xxx xx xx" />
              <Field label="Adres" multiline wide autoComplete="street-address" value={form.address} onChange={v => update("address", v)} placeholder="Mahalle, cadde, bina ve kapı numarası" />
              <Field label="İl" value={form.city} onChange={v => update("city", v)} placeholder="İstanbul" />
              <Field label="İlçe" value={form.district} onChange={v => update("district", v)} placeholder="Kadıköy" />
              <Field label="Posta Kodu" value={form.postal_code} onChange={v => update("postal_code", v)} placeholder="34000" maxLength={10} />
              <Field label="Faks" type="tel" value={form.fax} onChange={v => update("fax", v)} placeholder="İsteğe bağlı" />
            </div>
          </div>

          <div id="s03" className="cw-form-section">
            <div className="cw-section-title">
              <span>03</span>
              <div><h2>Finansal Bilgiler</h2><p>Para birimi ve varsa devir bakiyesi.</p></div>
            </div>
            <div className="cw-field-grid">
              <label className="cw-field">
                <span>Para Birimi</span>
                <select value={form.currency} onChange={e => update("currency", e.target.value)}>
                  <option value="TRY">TRY — Türk Lirası</option>
                  <option value="USD">USD — Amerikan Doları</option>
                  <option value="EUR">EUR — Euro</option>
                  <option value="GBP">GBP — İngiliz Sterlini</option>
                </select>
              </label>
              <Field label="Açılış Bakiyesi" type="number" step="0.01" value={form.opening_balance} onChange={v => update("opening_balance", v)} placeholder="0,00" hint="Pozitif = alacak, negatif = borç." />
              <Field label="IBAN" wide value={form.iban} onChange={v => update("iban", v)} placeholder="TR00 0000 0000 0000 0000 0000 00" maxLength={42} />
            </div>
          </div>

          <div id="s04" className="cw-form-section">
            <div className="cw-section-title">
              <span>04</span>
              <div><h2>Yetkili Kişi ve Notlar</h2><p>İletişim kuracağınız kişi ve ekip notları.</p></div>
            </div>
            <div className="cw-field-grid">
              <Field label="Yetkilinin Adı Soyadı" value={form.contact_person?.name} onChange={v => contact("name", v)} />
              <Field label="Yetkilinin Telefonu" type="tel" value={form.contact_person?.phone} onChange={v => contact("phone", v)} />
              <Field label="Yetkilinin E-postası" wide type="email" value={form.contact_person?.email} onChange={v => contact("email", v)} />
              <Field label="Hesap Notları" multiline wide value={form.notes} onChange={v => update("notes", v)} placeholder="Ödeme koşulları, özel anlaşmalar veya ekip notları…" />
            </div>
          </div>

          <div className="cw-editor-footer">
            <span>* Yalnızca ünvan / ad soyad zorunludur.</span>
            <button type="submit" disabled={saving} className="cw-primary">
              <Check size={14} />
              {saving ? "Kaydediliyor…" : editMode ? "Değişiklikleri Kaydet" : "Cari Hesabı Oluştur"}
            </button>
          </div>
        </form>

        <aside className="cw-editor-side">
          <div className="cw-preview">
            <div className="cw-preview-head">
              <span className="cw-profile-monogram" style={{background: iconBg, borderColor: 'transparent', color: iconColor}}>
                <RoleIcon size={20} />
              </span>
              <h3>{form.name || <span style={{color:'var(--cw-muted)'}}>Cari ünvanı</span>}</h3>
              <p>{roleLabel} · {isCompany ? "Tüzel kişi" : "Gerçek kişi"}</p>
            </div>
            <div className="cw-preview-body">
              <div className="cw-preview-line"><Mail size={11} /><span>{form.email || "E-posta eklenmedi"}</span></div>
              <div className="cw-preview-line"><Phone size={11} /><span>{form.phone || "Telefon eklenmedi"}</span></div>
              <div className="cw-preview-line"><MapPin size={11} /><span>{[form.district, form.city].filter(Boolean).join(", ") || "Adres eklenmedi"}</span></div>
              {(form.opening_balance !== "" && form.opening_balance !== undefined) && (
                <div className="cw-preview-balance">
                  <span>{balance > 0 ? "Tahsil edilecek" : balance < 0 ? "Ödenecek" : "Bakiyesiz"}</span>
                  <strong style={{color: balance > 0 ? '#16a34a' : balance < 0 ? '#c0392b' : 'var(--cw-ink)'}}>
                    {new Intl.NumberFormat("tr-TR", { style: "currency", currency: form.currency || "TRY" }).format(Math.abs(balance))}
                  </strong>
                  <small>Açılış bakiyesi</small>
                </div>
              )}
            </div>
          </div>
          <div className="cw-form-nav">
            <span className="cw-eyebrow">BU SAYFADA</span>
            {[["s01","01","Hesabın Kimliği"],["s02","02","İletişim ve Adres"],["s03","03","Finansal Bilgiler"],["s04","04","Yetkili ve Notlar"]].map(([id,n,l]) => (
              <a key={id} href={`#${id}`} onClick={e => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({behavior:'smooth'}); }}>
                <span>{n}</span>{l}
              </a>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
